import { useState, useEffect, useContext, useRef } from "react"
import PropTypes from "prop-types"

//Helpers
import { getAuthErrorMessage } from "../../../../helpers/authErrorMessage"
import { LogError } from "../../../../helpers/logger"

//PrimeReact
import { Button } from "primereact/button"
import { InputText } from "primereact/inputtext"
import { Messages } from "primereact/messages"

import Disenrollment from "./Disenrollment"
import { LoaderProgressBar } from "../../../../components/Loaders"
import {
  getAuth,
  multiFactor,
  TotpMultiFactorGenerator,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth"
import QRCode from "qrcode"

import styles from "../../../../styles/TwoFA.module.css"

import RefreshLogin from "./RefreshLogin"
import { UserContext } from "../../../../context/userContext"

export default function Totp(props) {
  const msgs = useRef(null)
  const userCtx = useContext(UserContext)

  const MAX_TIMER = 720000 // 12 minutes in milliseconds

  const {
    reloadUser,
    setEnableMfaEnrolledFactor,
    enrolledFactor,
    finalizationState,
  } = props

  const [pendingCodeVerification, setPendingCodeVerification] = useState(true)

  const [loading, setLoading] = useState(false)
  const [refreshLogin, setRefreshLogin] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const [totpData, setTotpData] = useState({})
  const [totpCode, setTotpCode] = useState("")

  const [timer, setTimer] = useState(MAX_TIMER)

  const auth = getAuth()
  const user = auth.currentUser

  const clearInlineMessage = () => {
    msgs.current?.clear()
  }

  const notifyInfo = (detail) => {
    clearInlineMessage()
    userCtx?.setNotificationInformation(detail)
  }

  const showError = (detail) => {
    clearInlineMessage()
    msgs.current?.show([
      {
        severity: "error",
        detail,
        sticky: true,
        closable: true,
      },
    ])
  }

  const isRecentLogin = async (user) => {
    const idTokenResult = await user.getIdTokenResult()
    const authTime = idTokenResult.claims.auth_time * 1000 // convert to ms
    const now = Date.now()

    // Firebase's threshold is ~5 minutes, so we use 4.5 minutes here to be safe against clock skew and network delays
    const REAUTH_THRESHOLD_MS = 4.5 * 60 * 1000

    return now - authTime < REAUTH_THRESHOLD_MS
  }

  const reauthenticate = async (password) => {
    try {
      const credential = EmailAuthProvider.credential(user.email, password)

      //Reauthenticate the user with the provided password to ensure they recently logged in before allowing them to proceed with TOTP enrollment
      await reauthenticateWithCredential(user, credential)

      //Process with TOTP enrollment flow after successful re-authentication
      await getTotpData()
    } catch (error) {
      LogError("Error during re-authentication:", error)
      showError("Re-authentication failed. Please refresh the page and try again.")
    } finally {
      setRefreshLogin(false)
      setLoading(false)
    }
  }

  const getTotpData = async () => {
    setLoading(true)
    try {
      await reloadUser()

      if (await isRecentLogin(user)) {
        //Create a new session and generate a secret for TOTP enrollment
        const multiFactorSession = await multiFactor(user).getSession()
        const totpSecret =
          await TotpMultiFactorGenerator.generateSecret(multiFactorSession)

        // Creates a standardized URL that authenticator apps can read
        const otpAuthUrl = totpSecret.generateQrCodeUrl(
          user.email || user.uid,
          "SOAR App",
        )

        // generate a QR image for display in the UI
        const qrCode = await QRCode.toDataURL(otpAuthUrl)

        setTotpData({ qrCode, totpSecret })
      } else {
        // If the user's last login was not recent, prompt them to re-authenticate
        setRefreshLogin(true)
      }
    } catch (error) {
      LogError("Error loading TOTP data:", error)
      setTotpData({})
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getTotpData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    let timerInterval, pollingInterval
    if (pendingCodeVerification) {
      timerInterval = setInterval(() => {
        setTimer((prev) => {
          if (prev > 1000) {
            return prev - 1000
          } else {
            clearInterval(timerInterval)
            setTotpData({})
            return 0
          }
        })
      }, 1000)
    }

    return () => {
      clearInterval(timerInterval)
      clearInterval(pollingInterval)
    }
  }, [pendingCodeVerification])

  const handleTOTPCodeSubmit = async () => {
    let verificationSucceeded = false
    setSubmitting(true)
    setPendingCodeVerification(false)
    try {
      const totpSecret = totpData?.totpSecret

      if (!totpSecret) {
        throw new Error("TOTP secret not found. Please try again.")
      }

      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
        totpSecret,
        totpCode,
      )

      await multiFactor(user).enroll(assertion, "SOAR App")

      // enrolledFactors is updated in place — no accounts:lookup round-trip needed
      const enrolledFactors = multiFactor(user).enrolledFactors
      const totpFactor = enrolledFactors.find((f) => f.factorId === "totp")

      if (totpFactor) {
        await setEnableMfaEnrolledFactor("totp")
        verificationSucceeded = true

        notifyInfo("TOTP enrollment finalized successfully") 
      } else {
        throw new Error("Failed to finalize TOTP enrollment")
      }
    } catch (error) {
      showError(getAuthErrorMessage(error, "Unable to verify the authentication code."))
    } finally {
      let reloadSucceeded = false
      try {
        await reloadUser()
        reloadSucceeded = true
      } catch (reloadError) {
        LogError("Error reloading user after TOTP verification:", reloadError)
        if (verificationSucceeded) {
          showError(
            "Verification completed, but we could not refresh this page. Please refresh and check your 2FA status.",
          )
        }
      }

      if (!verificationSucceeded || !reloadSucceeded) {
        setSubmitting(false)
      }
    }
  }

  const handleRetryBackendFinalize = async () => {
    setSubmitting(true)
    try {
      await setEnableMfaEnrolledFactor("totp")
      await reloadUser()
      notifyInfo("Account setup completed for TOTP two-factor authentication.")
    } catch (error) {
      showError(
        getAuthErrorMessage(
          error,
          "Unable to complete account setup. Please try again.",
        ),
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (enrolledFactor) {
    const enrollementDate = new Date(
      enrolledFactor.enrollmentTime,
    ).toDateString()
    return (
      <>
        <div className={styles.setupBlock}>
          <Messages ref={msgs} />
          <h3>✅ TOTP Two-factor Authentication already enabled</h3>
          <p>
            Enrolled on: <strong>{enrollementDate}</strong>
          </p>
          {finalizationState?.pending && (
            <>
              <p className={styles.inlineErrorText}>
                Account setup is not complete yet. You can retry finalization without reenrolling this factor.
              </p>
              {finalizationState?.error ? (
                <p className={styles.inlineErrorText}>{finalizationState.error}</p>
              ) : null}
              <Button
                type="button"
                label="Retry setup"
                onClick={handleRetryBackendFinalize}
                loading={submitting}
                disabled={submitting}
                className={styles.primaryActionButton}
              />
            </>
          )}
        </div>
        <Disenrollment factorId={"totp"} />
      </>
    )
  }

  if (loading || refreshLogin) {
    return (
      <div className={styles.setupBlock}>
        <LoaderProgressBar />
        <Messages ref={msgs} />
        {refreshLogin && (
          <RefreshLogin
            onHide={(password) => {
              if (password) {
                setLoading(true)
                reauthenticate(password)
              } else {
                setRefreshLogin(false)
              }
            }}
          />
        )}
      </div>
    )
  }

  if (Object.keys(totpData).length === 0) {
    return (
      <div className={styles.setupBlock}>
        <Messages ref={msgs} />
        <div className={styles.block}>
          <h3>
            Failed to load TOTP data. Please log in again.{" "}
            <button
              className={styles.underlinedLink}
              onClick={() => auth.signOut()}
            >
              Click here to re-login
            </button>
            .
          </h3>
        </div>
      </div>
    )
  }

  const formatTime = (milliseconds) => {
    const minutes = Math.floor(milliseconds / 60000)
    const seconds = ((milliseconds % 60000) / 1000).toFixed(0)
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`
  }

  return (
    <div className={styles.setupBlock}>
      <Messages ref={msgs} />
      <div className={styles.block}>
        <h3>Configure your TOTP multi-factor authentication</h3>
        <p>
          Time-based One-Time Password (TOTP) is a 2FA method that should be
          completed within <strong>12 minutes</strong>. If you start the process
          and the time expires, or if there is an error, you will be asked to
          re-login.
        </p>
        <strong className={styles.timerLabel}>
          Remaining Time: {formatTime(timer)}
        </strong>
        <div>Scan the QR code below to enable TOTP:</div>
        {totpData?.qrCode && <img src={totpData.qrCode} alt="QR Code" />}
        <div>Enter the code below to verify:</div>
        <InputText
          value={totpCode}
          disabled={submitting}
          onChange={(e) => setTotpCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && totpCode.trim() !== "" && !submitting) {
              e.preventDefault()
              handleTOTPCodeSubmit()
            }
          }}
        />
        <Button
          type="button"
          label="Verify"
          onClick={handleTOTPCodeSubmit}
          loading={submitting}
          className={`${styles.marginLeft5} ${styles.primaryActionButton}`}
          disabled={submitting || totpCode.trim() === ""}
        />
      </div>
    </div>
  )
}

Totp.propTypes = {
  reloadUser: PropTypes.func.isRequired,
  setEnableMfaEnrolledFactor: PropTypes.func.isRequired,
  enrolledFactor: PropTypes.object,
  finalizationState: PropTypes.shape({
    pending: PropTypes.bool,
    error: PropTypes.string,
  }),
}
